import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import {
  ArrowLeft, ArrowRight, TrendingUp, TrendingDown, Minus,
  Plus, X as XIcon, Check, Loader2, Sparkles, Award
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Logo } from '../components/Logo';
import { Button } from '../components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Friendly question id labels
const QID_LABELS = {
  age: 'Age',
  gender: 'Sex',
  country: 'Country',
  ancestry: 'Ancestry',
  activity_level: 'Activity Level',
  height: 'Height',
  weight: 'Weight',
  health_conditions: 'Health Conditions',
  sleep_hours: 'Sleep Hours',
  stress_level: 'Stress',
  energy_level: 'Energy',
  diet_type: 'Diet',
  smoking: 'Smoking',
  alcohol: 'Alcohol',
};
const niceLabel = (id) =>
  QID_LABELS[id] || id.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

const CATEGORY_LABELS = {
  energy: 'Energy',
  sleep: 'Sleep',
  stress: 'Stress',
  immunity: 'Immunity',
  digestion: 'Digestion',
  heart: 'Heart',
  brain: 'Brain',
  bone: 'Bone',
  hair_skin: 'Hair & Skin',
  weight: 'Weight',
  mood: 'Mood',
  vision: 'Vision',
  menopause: 'Menopause',
  fertility: 'Fertility',
};

export default function WhatChangedPage() {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user, getAuthHeader, loading: authLoading } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!authLoading && !user) navigate('/auth?mode=login');
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/assessments/compare`, { headers: getAuthHeader() });
        if (!res.data.comparison) {
          // Only one assessment exists — fall through to results
          navigate(`/results/${id}`, { replace: true });
          return;
        }
        setData(res.data.comparison);
      } catch (e) {
        console.error('compare failed:', e);
        setError(e.message || 'Failed to load comparison');
      } finally {
        setLoading(false);
      }
    })();
  }, [user, id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background px-6">
        <p className="text-muted-foreground mb-4">{error || 'No comparison available.'}</p>
        <Button className="btn-primary" onClick={() => navigate(`/results/${id}`)}>
          Continue to Results
        </Button>
      </div>
    );
  }

  const { changes = {}, supplements = {}, changed_answers = [] } = data;

  // Improved categories (most positive deltas)
  const scoreEntries = Object.entries(changes).map(([cat, c]) => ({ cat, ...c }));
  const improved = scoreEntries
    .filter((s) => s.change > 0)
    .sort((a, b) => b.change - a.change)
    .slice(0, 5);
  const declined = scoreEntries
    .filter((s) => s.change < 0)
    .sort((a, b) => a.change - b.change)
    .slice(0, 5);

  const dosageChanges = (supplements.kept || []).filter((s) => s.dosage_changed);
  const totalChanges =
    (supplements.added?.length || 0) +
    (supplements.removed?.length || 0) +
    dosageChanges.length +
    changed_answers.length +
    improved.length +
    declined.length;

  return (
    <div className="min-h-screen bg-background" data-testid="what-changed-page">
      {/* Header */}
      <header className="border-b border-border bg-white sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary"
          >
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </button>
          <Logo />
          <div className="w-20" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-10"
        >
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-primary/10 flex items-center justify-center">
            <Sparkles className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-[#1A1A1A] mb-2">
            Here's what changed
          </h1>
          <p className="text-muted-foreground">
            {totalChanges === 0
              ? 'No major changes since your last assessment — your plan stays the same.'
              : `${totalChanges} update${totalChanges !== 1 ? 's' : ''} from your last assessment`}
          </p>
        </motion.div>

        {/* Health Score Trends */}
        {(improved.length > 0 || declined.length > 0) && (
          <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
            className="mb-8 p-5 sm:p-6 bg-white rounded-2xl border border-border/50"
            data-testid="what-changed-scores"
          >
            <h2 className="text-lg font-semibold text-[#1A1A1A] mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" /> Your Health Scores
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {improved.map((s) => (
                <ScoreRow key={s.cat} s={s} direction="up" />
              ))}
              {declined.map((s) => (
                <ScoreRow key={s.cat} s={s} direction="down" />
              ))}
            </div>
          </motion.section>
        )}

        {/* Supplements diff */}
        {(supplements.added?.length > 0 ||
          supplements.removed?.length > 0 ||
          dosageChanges.length > 0) && (
          <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-8 p-5 sm:p-6 bg-white rounded-2xl border border-border/50"
            data-testid="what-changed-supplements"
          >
            <h2 className="text-lg font-semibold text-[#1A1A1A] mb-4 flex items-center gap-2">
              <Award className="w-5 h-5 text-primary" /> Plan Updates
            </h2>

            {supplements.added?.length > 0 && (
              <div className="mb-4" data-testid="supps-added">
                <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700 mb-2 flex items-center gap-1">
                  <Plus className="w-3.5 h-3.5" /> Added ({supplements.added.length})
                </p>
                <div className="space-y-1.5">
                  {supplements.added.map((s, i) => (
                    <div
                      key={i}
                      className="px-3 py-2 bg-emerald-50 border border-emerald-100 rounded-lg flex items-center gap-2"
                    >
                      <Plus className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                      <span className="text-sm font-medium text-[#1A1A1A]">{s.name}</span>
                      {s.dosage && (
                        <span className="text-xs text-muted-foreground">· {s.dosage}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {supplements.removed?.length > 0 && (
              <div className="mb-4" data-testid="supps-removed">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-2 flex items-center gap-1">
                  <XIcon className="w-3.5 h-3.5" /> No longer needed ({supplements.removed.length})
                </p>
                <div className="space-y-1.5">
                  {supplements.removed.map((s, i) => (
                    <div
                      key={i}
                      className="px-3 py-2 bg-muted/30 border border-border/40 rounded-lg flex items-center gap-2"
                    >
                      <XIcon className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
                      <span className="text-sm font-medium text-muted-foreground line-through">{s.name}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {dosageChanges.length > 0 && (
              <div data-testid="supps-dosage-changed">
                <p className="text-xs font-semibold uppercase tracking-wider text-amber-700 mb-2 flex items-center gap-1">
                  <Minus className="w-3.5 h-3.5" /> Dosage adjusted ({dosageChanges.length})
                </p>
                <div className="space-y-1.5">
                  {dosageChanges.map((s, i) => (
                    <div
                      key={i}
                      className="px-3 py-2 bg-amber-50 border border-amber-100 rounded-lg flex items-center gap-2 flex-wrap"
                    >
                      <span className="text-sm font-medium text-[#1A1A1A]">{s.name}</span>
                      <span className="text-xs text-muted-foreground line-through">{s.prev_dosage}</span>
                      <ArrowRight className="w-3 h-3 text-amber-600" />
                      <span className="text-xs font-semibold text-amber-700">{s.dosage}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </motion.section>
        )}

        {/* Answer changes (lifestyle/profile) */}
        {changed_answers.length > 0 && (
          <motion.section
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
            className="mb-8 p-5 sm:p-6 bg-white rounded-2xl border border-border/50"
            data-testid="what-changed-answers"
          >
            <h2 className="text-lg font-semibold text-[#1A1A1A] mb-4 flex items-center gap-2">
              <Check className="w-5 h-5 text-primary" /> What You Told Us Changed
            </h2>
            <div className="space-y-2">
              {changed_answers.slice(0, 10).map((a, i) => (
                <div key={i} className="px-3 py-2 bg-muted/20 rounded-lg">
                  <p className="text-xs font-semibold text-[#1A1A1A] mb-1">{niceLabel(a.question_id)}</p>
                  <div className="flex items-center gap-2 text-xs flex-wrap">
                    <span className="text-muted-foreground line-through">{formatAnswer(a.previous)}</span>
                    <ArrowRight className="w-3 h-3 text-primary" />
                    <span className="font-semibold text-primary">{formatAnswer(a.current)}</span>
                  </div>
                </div>
              ))}
              {changed_answers.length > 10 && (
                <p className="text-xs text-muted-foreground italic pl-1">
                  + {changed_answers.length - 10} more updates
                </p>
              )}
            </div>
          </motion.section>
        )}

        {/* No changes path */}
        {totalChanges === 0 && (
          <div className="mb-8 p-8 bg-white rounded-2xl border border-border/50 text-center">
            <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-emerald-100 flex items-center justify-center">
              <Check className="w-6 h-6 text-emerald-600" />
            </div>
            <p className="text-[#1A1A1A] font-medium mb-1">Your plan is steady</p>
            <p className="text-sm text-muted-foreground">
              We didn't find any changes that warrant updating your supplement plan.
            </p>
          </div>
        )}

        {/* CTA */}
        <div className="flex flex-col sm:flex-row gap-3 mt-10">
          <Button
            className="btn-primary flex-1"
            onClick={() => navigate(`/results/${id}`)}
            data-testid="view-full-results-btn"
          >
            View Your Full Plan <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <Button variant="outline" className="flex-1" onClick={() => navigate('/my-plan')}>
            Go to My Plan
          </Button>
        </div>
      </main>
    </div>
  );
}

function ScoreRow({ s, direction }) {
  const up = direction === 'up';
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <div
      className={`px-3 py-2.5 rounded-xl border flex items-center gap-3 ${
        up ? 'bg-emerald-50 border-emerald-100' : 'bg-rose-50 border-rose-100'
      }`}
    >
      <div
        className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
          up ? 'bg-emerald-100' : 'bg-rose-100'
        }`}
      >
        <Icon className={`w-4 h-4 ${up ? 'text-emerald-600' : 'text-rose-600'}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-[#1A1A1A] capitalize">
          {CATEGORY_LABELS[s.cat] || s.cat}
        </p>
        <p className="text-xs text-muted-foreground">
          {s.previous} → <span className="font-semibold text-[#1A1A1A]">{s.current}</span>
        </p>
      </div>
      <span
        className={`text-sm font-bold ${up ? 'text-emerald-700' : 'text-rose-700'}`}
      >
        {up ? '+' : ''}{s.change}
      </span>
    </div>
  );
}

function formatAnswer(v) {
  if (v === null || v === undefined) return '—';
  if (Array.isArray(v)) return v.join(', ');
  if (typeof v === 'object') {
    if (v.value && v.unit) return `${v.value} ${v.unit}`;
    return JSON.stringify(v);
  }
  return String(v);
}
